import {
  Button,
  HStack,
  Menu,
  MenuButton,
  MenuDivider,
  MenuGroup,
  MenuItem,
  MenuList,
  Portal,
  Text,
  useToast,
} from "@chakra-ui/react";
import { FaChevronDown } from "react-icons/fa";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { routes } from "config";
import { Issb } from "../../../../api";
import { ListBullets, Pen, PlayPause, Trash } from "@phosphor-icons/react";
import { useGetStagesOfRound } from "../../../../hooks/query";
import { showErrorToast, showSuccessToast } from "../../../../utils";
import { useState, Fragment } from "react";
import DevOnly from "context/Auth/DevOnly";
import useAirUserRoles from "utils/AuthUtils";
import { hideAppSpinner, showAppSpinner } from "../../../../context/Store/slices/appSpinnerSlice";
import questionnaireService from "../../../../api/issb/questionnaire.service";
import { useRefreshRoundList, useSelectRound } from "../../QuestionnaireRound/RoundService";
import { useTranslation } from "react-i18next";
/**
 * Navbar dropdown for actions on the ISSB page
 */
const ActionList = () => {
  const toast = useToast();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { refreshRoundList } = useRefreshRoundList();
  const selectRound = useSelectRound();

  const { t } = useTranslation("translation", {
    keyPrefix: "issb",
  });
  const [auditStages, setAuditStages] = useState([]);

  const { isController, isOrgAdmin } = useAirUserRoles();

  const roundSelector = useSelector((state) => state.issbRound);

  const questionnaireId = roundSelector.selectedRound?.id;

  useGetStagesOfRound(questionnaireId, {
    enabled: !!questionnaireId, // Fetch only if questionnaire is selected
    onSuccess: (data) => {
      const stages = data.filter((stage) => stage.stageType === "AUDIT");
      setAuditStages(stages);
    },
    onError: (err) => showErrorToast(toast, err.response?.data?.parameters?.message ?? err.message),
    cacheTime: 0,
  });

  const validateControl = () => {
    const currentRound = roundSelector?.selectedRound;
    if (!currentRound) {
      toast({
        title: "Error",
        description: "Please select a round to start an audit",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
      return false;
    }
    return true;
  };

  const handleStartAudit = (stage) => {
    if (!validateControl()) return;
    navigate(`${routes.issbStartAudit}?stageId=${stage?.id}&stageName=${stage?.name}`);
  };

  const handleCloseAudit = (stage) => {
    if (!validateControl()) return;
    navigate(`${routes.issbCloseAudit}?stageId=${stage?.id}&stageName=${stage?.name}`);
  };

  const handleConfigureStage = () => {
    navigate(routes.issbStageSetup);
  };

  const handleAIReview = () => {
    navigate(routes.aiReview);
  };

  // Note: This is a dev-only action to let us clean-up workspaces, esp demo. Clients can close a round, but not delete it.
  const handleDeleteRound = () => {
    console.warn("Delete round!", roundSelector?.selectedRound);
    Issb.deleteQuestionnaireRound(roundSelector?.selectedRound.id);
  };

  const getRoundActionMenuLabel = () => {
    const status = roundSelector?.selectedRound?.state;
    switch (status) {
      case "DRAFT":
        return "Start Round";
      case "CLOSED":
        return "Reopen Round";
      case "ACTIVE":
        return "Close Round";
      //   default will be backward compatible with old rounds without status
      default:
        return "Start Round";
    }
  };

  const handleStartCloseRound = () => {
    const selectedRound = roundSelector?.selectedRound;
    if (!selectedRound) return;
    const qround = { ...selectedRound };
    switch (selectedRound?.state) {
      case "DRAFT":
        qround.state = "ACTIVE";
        break;
      case "ACTIVE":
        qround.state = "CLOSED";
        break;
      case "CLOSED":
        qround.state = "ACTIVE";
        break;
      //   default will be backward compatible with old rounds without status
      default:
        qround.state = "ACTIVE";
        break;
    }

    dispatch(showAppSpinner());
    questionnaireService
      .create(qround)
      .then((qround2) => {
        refreshRoundList().then(() => {
          selectRound(qround2);
          showSuccessToast(toast, t("general.success"));
          setTimeout(() => {
            navigate(routes.issbRequirements);
          }, 500);
        });
      })
      .catch((err) => {
        console.error(err);
        showErrorToast(toast, t("general.error"));
      })
      .finally(() => {
        dispatch(hideAppSpinner());
      });
  };

  let auditDisabledReason = null;
  if (!isController()) auditDisabledReason = "Only controllers can start audits";
  else if (!roundSelector?.selectedRound) auditDisabledReason = "Please select a round";
  else if (roundSelector?.selectedRound?.state === "DRAFT") auditDisabledReason = "First start the Round";

  return (
    <Menu>
      <MenuButton as={Button} variant="outline" rightIcon={<FaChevronDown />}>
        <HStack>
          <Text
            fontSize="14px" // TODO: temp fix with fixed font size, chakra theme value is not working here
            display={{ base: "none", lg: "block" }}
          >
            Round Actions
          </Text>
        </HStack>
      </MenuButton>
      <Portal>
        <MenuList zIndex={9999}>
          {/* Only controller can start / close audits */}
          <MenuGroup title="Audits">
            {auditStages.map((stage) => (
              <Fragment key={stage?.id}>
                <MenuItem
                  onClick={() => handleStartAudit(stage)}
                  isDisabled={!!auditDisabledReason}
                  title={auditDisabledReason || "Start Audit: " + stage?.name}
                >
                  Start {stage?.name}
                </MenuItem>
                <MenuItem
                  onClick={() => handleCloseAudit(stage)}
                  isDisabled={!!auditDisabledReason}
                  title={auditDisabledReason || "Close Audit: " + stage?.name}
                >
                  Close {stage?.name}
                </MenuItem>
              </Fragment>
            ))}
          </MenuGroup>
          <MenuDivider />{" "}
          <MenuGroup title="AI">
            <MenuItem onClick={() => handleAIReview()} isDisabled={!roundSelector?.selectedRound}>
              AI Review
            </MenuItem>
          </MenuGroup>
          <MenuDivider />
          <MenuGroup title="Configurations">
            <MenuItem
              icon={<PlayPause size={18} />}
              isDisabled={!roundSelector?.selectedRound || !isController()}
              title={isController() ? "Start Round" : "Only controllers can start or close rounds"}
              onClick={() => handleStartCloseRound()}
            >
              {getRoundActionMenuLabel()}
            </MenuItem>
            <MenuItem
              onClick={() => handleConfigureStage()}
              isDisabled={!roundSelector?.selectedRound}
              icon={<ListBullets size={18} />}
              disabled={!roundSelector?.selectedRound || !isController()}
            >
              Configure Stages
            </MenuItem>
            <MenuItem
              onClick={() => navigate(`${routes.issbQuestionnaireRoundSetup}?edit=true`)}
              isDisabled={!roundSelector?.selectedRound || (!isController() && !isOrgAdmin)}
              icon={<Pen size={18} />}
            >
              Edit Round
            </MenuItem>
            <DevOnly>
              <MenuItem
                color="red"
                icon={<Trash size={18} />}
                onClick={handleDeleteRound}
                title={isController() ? "Delete Round" : "Only controllers can delete rounds"} // Note: actually "archive" but the user can't restore yet, so "delete" is a safer label to show
                isDisabled={!roundSelector?.selectedRound || (!isController() && !isOrgAdmin)}
              >
                Delete this round
              </MenuItem>
            </DevOnly>
          </MenuGroup>
        </MenuList>
      </Portal>
    </Menu>
  );
};

export default ActionList;
